import React from "react";
import { Link } from "react-router-dom";
import type { UseQueryResult } from "@tanstack/react-query";
import { CheckCircle2, CircleDashed, Link2 } from "lucide-react";
import type { CommunityIssueActivityRow } from "lib/api/community";
import type { CommunityUiStrings } from "i18n/communityUi";
import EntityAvatar from "components/Entities/EntityAvatar";
import SectionShell from "../SectionShell";
import {
  SectionEmptyState,
  SectionErrorState,
  SectionLoadingState,
} from "../SectionStates";
import {
  buildIssueHref,
  getIssueCoverageState,
} from "../utils";

interface PriorityFocusProps {
  query: UseQueryResult<CommunityIssueActivityRow[]>;
  labels: CommunityUiStrings;
  numberFormatter: Intl.NumberFormat;
}

function formatParetoSummary(template: string, count: number, share: number): string {
  return template
    .replace("{count}", String(count))
    .replace("{share}", String(Math.round(share)));
}

/**
 * Pareto-style ranked issue portfolio.
 */
const PriorityFocus: React.FC<PriorityFocusProps> = ({
  query,
  labels,
  numberFormatter,
}) => {
  return (
    <SectionShell
      title={labels.priorityFocusTitle}
      subtitle={labels.priorityFocusSubtitle}
      actions={
        <Link
          to="/search?type=issues"
          className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
        >
          {labels.viewIssues}
        </Link>
      }
      contentClassName="space-y-5"
    >
      {query.isPending ? (
        <SectionLoadingState message={labels.loading} />
      ) : query.isError ? (
        <SectionErrorState
          message={labels.sectionError}
          retryLabel={labels.retry}
          onRetry={() => {
            void query.refetch();
          }}
          isRetrying={query.isRefetching}
        />
      ) : query.data && query.data.length > 0 ? (
        (() => {
          const rows = query.data.slice(0, 10);
          const maxActivity = Math.max(...rows.map((row) => row.activity_total), 1);
          const rankedTotal = query.data.reduce((sum, row) => sum + row.activity_total, 0);
          const displayedTotal = rows.reduce((sum, row) => sum + row.activity_total, 0);
          const sharePct = rankedTotal > 0 ? (displayedTotal / rankedTotal) * 100 : 0;

          return (
            <>
              <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                {formatParetoSummary(labels.priorityParetoSummary, rows.length, sharePct)}
              </p>
              <div className="space-y-3">
                {rows.map((row) => {
                  const coverageState = getIssueCoverageState(row);
                  const widthPct = Math.round((row.activity_total / maxActivity) * 100);
                  const coverageLabel =
                    coverageState === "complete"
                      ? labels.priorityCoverageComplete
                      : coverageState === "missing_solution"
                        ? labels.priorityMissingSolution
                        : coverageState === "missing_action"
                          ? labels.priorityMissingAction
                          : labels.priorityMissingBoth;
                  const coverageTone =
                    coverageState === "complete"
                      ? "border-emerald-300/70 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-950/30 dark:text-emerald-200"
                      : coverageState === "missing_both"
                        ? "border-amber-300/70 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-200"
                        : "border-blue-300/70 bg-blue-50 text-blue-700 dark:border-blue-500/40 dark:bg-blue-950/30 dark:text-blue-200";

                  return (
                    <div
                      key={row.issue_id}
                      className="rounded-2xl border border-eoe-border/80 bg-eoe-bg-hover/25 p-4 dark:border-eoe-dark-border/80 dark:bg-eoe-dark-bg-hover/15"
                    >
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                        <div className="min-w-0 flex-1 space-y-3">
                          <div className="flex flex-wrap items-center gap-3">
                            <div className="min-w-0 flex-1">
                              <EntityAvatar
                                entityId={row.issue_id}
                                entityType="issue"
                                showLink
                                isInputField={false}
                                hasRing={false}
                              />
                            </div>
                            <span
                              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${coverageTone}`}
                            >
                              {coverageState === "complete" ? (
                                <CheckCircle2 className="size-3.5" aria-hidden />
                              ) : (
                                <CircleDashed className="size-3.5" aria-hidden />
                              )}
                              {coverageLabel}
                            </span>
                          </div>
                          <div className="space-y-2">
                            <div className="flex items-center justify-between text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                              <span>{labels.activityLayerTotal}</span>
                              <span>{numberFormatter.format(row.activity_total)}</span>
                            </div>
                            <div className="h-3 overflow-hidden rounded-full bg-eoe-border/70 dark:bg-eoe-dark-border/70">
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-blue-600 via-violet-500 to-amber-500"
                                style={{ width: `${widthPct}%` }}
                              />
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-4 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                            <span>{labels.contributorComments}: {numberFormatter.format(row.comment_count)}</span>
                            <span>{labels.contributorPosts}: {numberFormatter.format(row.post_count)}</span>
                            <span>{labels.activityLayerAnalysis}: {numberFormatter.format(row.relation_count)}</span>
                          </div>
                        </div>
                        <Link
                          to={buildIssueHref(row.issue_cname, row.issue_name)}
                          className="inline-flex items-center justify-center gap-2 rounded-full border border-eoe-border/80 px-4 py-2 text-sm font-medium text-eoe-text transition-colors hover:bg-eoe-bg-hover dark:border-eoe-dark-border/80 dark:text-eoe-dark-text dark:hover:bg-eoe-dark-bg-hover"
                        >
                          <Link2 className="size-4" aria-hidden />
                          {labels.openIssue}
                        </Link>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()
      ) : (
        <SectionEmptyState title={labels.emptyPriorityFocus} />
      )}
    </SectionShell>
  );
};

export default PriorityFocus;

